import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Mail, Store, User, Lock, Eye, EyeOff, Shield } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "../../../lib/auth-context";
import { useSubscription } from "../../../lib/subscription-context";
import { api, getErrorMessage } from "../../../lib/api";

const USER_TYPE_LABELS = { 1: "Admin", 2: "Manager", 3: "User" };

export const ProfilePage = () => {
  const { user, updateUser, logout } = useAuth();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [sellerName, setSellerName] = useState(user?.sellerName || "");
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/delete-user", { data: { userId: user.id } });
      toast.success("Account deleted");
      await logout();
      navigate("/");
    } catch (err) {
      toast.error("Couldn't delete account", { description: getErrorMessage(err) });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch = { name, email, sellerName };
      if (password) patch.password = password;
      await updateUser(patch);
      setPassword("");
      toast.success("Profile updated");
    } catch (err) {
      toast.error("Update failed", { description: getErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="panel-profile">
      <div className="lg:col-span-2">
        <form
          onSubmit={save}
          className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8"
          data-testid="profile-form"
        >
          <div className="font-heading text-lg font-extrabold text-slate-900">Account details</div>
          <div className="text-sm text-slate-500 mt-1">Update your personal and store information.</div>

          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2 flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center font-heading text-2xl font-extrabold">
                {(name?.[0] || "S").toUpperCase()}
              </div>
              <div>
                <div className="font-heading text-base font-bold text-slate-900">{name || "Seller"}</div>
                <div className="text-xs text-slate-500">{email}</div>
              </div>
            </div>

            <div>
              <Label htmlFor="pf-name" className="text-sm font-semibold text-slate-700">Full name</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <User className="w-4 h-4" />
                </span>
                <Input
                  id="pf-name"
                  data-testid="profile-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-11 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pf-seller" className="text-sm font-semibold text-slate-700">
                Store / Seller name
              </Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Store className="w-4 h-4" />
                </span>
                <Input
                  id="pf-seller"
                  data-testid="profile-seller-input"
                  value={sellerName}
                  onChange={(e) => setSellerName(e.target.value)}
                  placeholder="Your brand / store name"
                  className="h-11 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pf-email" className="text-sm font-semibold text-slate-700">Email</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Mail className="w-4 h-4" />
                </span>
                <Input
                  id="pf-email"
                  data-testid="profile-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pf-pw" className="text-sm font-semibold text-slate-700">
                New password <span className="text-slate-400 font-normal">· optional</span>
              </Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Lock className="w-4 h-4" />
                </span>
                <Input
                  id="pf-pw"
                  data-testid="profile-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave blank to keep current"
                  className="h-11 pl-10 pr-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-500 hover:text-purple-700 hover:bg-purple-50"
                  data-testid="profile-password-toggle"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-7 flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              data-testid="profile-save-button"
              className="rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold h-11 px-6 shadow-lg shadow-purple-600/25"
            >
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>

      <div className="space-y-4">
        <div className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50 p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-purple-700">
            <Shield className="w-3.5 h-3.5" /> Account
          </div>
          <div className="mt-3 space-y-2.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Account type</span>
              <span className="font-bold text-slate-900">{USER_TYPE_LABELS[user?.userType] || "User"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Plan</span>
              <span className="font-bold text-purple-700 capitalize">{subscription?.plan ?? "No plan"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Joined</span>
              <span className="font-medium text-slate-900">
                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Today"}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-red-100 bg-white p-6">
          <div className="text-xs uppercase tracking-wider font-bold text-red-600">Danger zone</div>
          <p className="text-sm text-slate-600 mt-2">
            Deleting your account is permanent. Your subscription will be cancelled immediately.
          </p>
          <Button
            variant="outline"
            onClick={() => setConfirmDelete(true)}
            data-testid="profile-delete-account"
            className="mt-4 rounded-full border-red-200 text-red-600 hover:bg-red-50 font-semibold"
          >
            Delete account
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="profile-delete-confirm" className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Your account, subscription, and listings access will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="profile-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteAccount}
              disabled={deleting}
              data-testid="profile-delete-confirm-button"
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Deleting…" : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
